/**
 * Knows whether the portal has been taken offline for maintenance mid-session.
 *
 * A fresh load learns this from GET /api/maintenance (see MaintenanceGate). But
 * a tab that is already open would just start failing every call when we bring
 * the portal down, so the API client watches for the 503 that the backend
 * returns while offline and tells this store. The gate subscribes and swaps the
 * whole app for the notice, no reload needed.
 *
 * Only ever flips toward "down": coming back up is a reload for the customer,
 * which re-runs the startup check.
 */

interface State {
  down: boolean
  message: string
}

type Listener = (state: State) => void

class MaintenanceStore {
  private down = false

  private message = ''

  private listeners = new Set<Listener>()

  isDown(): boolean {
    return this.down
  }

  getMessage(): string {
    return this.message
  }

  /** Record that a response signalled maintenance. */
  observed(message?: string | null): void {
    const nextMessage = message ?? this.message

    if (this.down && nextMessage === this.message) return

    this.down = true
    this.message = nextMessage
    this.emit()
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener)
    return () => {
      this.listeners.delete(listener)
    }
  }

  private emit(): void {
    const state: State = { down: this.down, message: this.message }
    this.listeners.forEach(listener => listener(state))
  }
}

export const maintenanceStore = new MaintenanceStore()
